import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { AiOutlineCloseCircle, AiOutlineInfoCircle, AiOutlineCheckCircle, AiOutlineWarning } from 'react-icons/ai';

type ToastVariant = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const TOAST_DURATION_MS = 4000;

const ToastViewport = styled.div`
  position: fixed;
  top: var(--space-4);
  right: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  z-index: 3000;
  width: min(360px, calc(100vw - 32px));

  @media (max-width: 640px) {
    top: auto;
    right: var(--space-3);
    bottom: calc(72px + var(--space-3));
    left: var(--space-3);
    width: auto;
  }
`;

const toastVariantStyles: Record<ToastVariant, ReturnType<typeof css>> = {
  success: css`
    border-left: 4px solid #27ae60;
  `,
  error: css`
    border-left: 4px solid #c0392b;
  `,
  info: css`
    border-left: 4px solid var(--highlight-bg-color);
  `,
};

const getToastVariantStyles = ({ $variant }: { $variant: ToastVariant }) =>
  toastVariantStyles[$variant];

const ToastCard = styled.div<{ $variant: ToastVariant }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  gap: var(--space-3);
  background: var(--bg-color-content);
  color: var(--text-main);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-color);
  ${getToastVariantStyles}
`;

const ToastMessage = styled.p`
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: 1.45;
`;

const ToastIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-top: 2px;
`;

const ToastDismissButton = styled.button`
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;

  &:hover {
    color: var(--text-main);
  }
`;

const getToastIcon = (variant: ToastVariant) => {
  switch (variant) {
    case 'success':
      return <AiOutlineCheckCircle size={18} aria-hidden="true" />;
    case 'error':
      return <AiOutlineWarning size={18} aria-hidden="true" />;
    default:
      return <AiOutlineInfoCircle size={18} aria-hidden="true" />;
  }
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextIdRef = useRef(1);
  const timeoutRefs = useRef<Record<number, number>>({});

  const removeToast = useCallback((id: number) => {
    const timeoutId = timeoutRefs.current[id];
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      delete timeoutRefs.current[id];
    }

    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const pushToast = useCallback((variant: ToastVariant, message: string) => {
    const id = nextIdRef.current++;
    setToasts((current) => [...current, { id, message, variant }]);

    timeoutRefs.current[id] = window.setTimeout(() => {
      removeToast(id);
    }, TOAST_DURATION_MS);
  }, [removeToast]);

  useEffect(() => {
    return () => {
      Object.values(timeoutRefs.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      timeoutRefs.current = {};
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({
    success: (message: string) => pushToast('success', message),
    error: (message: string) => pushToast('error', message),
    info: (message: string) => pushToast('info', message),
  }), [pushToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => (
          <ToastCard key={toast.id} $variant={toast.variant} role="status">
            <ToastIcon>{getToastIcon(toast.variant)}</ToastIcon>
            <ToastMessage>{toast.message}</ToastMessage>
            <ToastDismissButton
              type="button"
              aria-label="Fechar notificação"
              onClick={() => removeToast(toast.id)}
            >
              <AiOutlineCloseCircle size={18} />
            </ToastDismissButton>
          </ToastCard>
        ))}
      </ToastViewport>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }

  return context;
};
